import { createAuth0Client } from "@auth0/auth0-spa-js";
import { decodeJwtPayload } from "@/client/utils/decodeJwtPayload";
import { showWebAuthGate } from "@/client/core/components/web-auth-gate.component";
import {
    registerRequestAuthToken,
    setAuthToken,
    expectAuthToken,
} from "@/client/core/services/auth-token.signal";
import {
    refreshSpliceProfile,
    registerWebLogout,
    setWebAccount,
} from "@/client/core/services/web-account.signal";

const kAudience = "https://splice.com";
const kDefaultAuth0Domain = "auth.splice.com";

export interface WebAuthConfig {
    domain: string;
    clientId: string;
}

/**
 * The web login gate is active only when an Auth0 client ID is configured
 * (see docs/05-auth0-setup.md for the one-time Auth0 application setup).
 * Without it the website stays ungated, matching previous behavior.
 */
export const getWebAuthConfig = (): WebAuthConfig | undefined => {
    const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
    if (typeof clientId !== "string" || clientId === "") {
        return undefined;
    }
    const domain = import.meta.env.VITE_AUTH0_DOMAIN;
    return {
        domain: typeof domain === "string" && domain !== "" ? domain : kDefaultAuth0Domain,
        clientId,
    };
};

/**
 * Decodes the `exp` claim from an Auth0 JWT (in ms since epoch). Returns 0 if
 * the token is malformed or missing an exp.
 */
const decodeJwtExp = (token: string): number => {
    const exp = decodeJwtPayload(token)?.exp;
    return typeof exp === "number" ? exp * 1000 : 0;
};

interface RedirectAppState {
    returnTo?: string;
}

/**
 * Boot-time entry point for every page (the home directory has no Core, apps
 * do — both must gate). No-op in iframes/plugins (tokens arrive over the
 * MessageChannel there) and when no Auth0 client ID is configured.
 */
/**
 * Whether this page has an account of its own to show: a top-level website
 * page with the Auth0 gate configured. False in the plugin surfaces (the host
 * owns the account) and on an ungated web build (no client id), where the
 * chrome that depends on knowing who is signed in should stay as it was.
 */
export const hasWebAccount = (): boolean =>
    typeof window !== "undefined" && window.parent === window && getWebAuthConfig() !== undefined;

export const initWebAuthGateIfNeeded = (onError: (error: unknown) => void): void => {
    if (typeof window === "undefined" || window.parent !== window) {
        return;
    }
    const config = getWebAuthConfig();
    if (config === undefined) {
        return;
    }
    // Requests that fire during boot (the first events, a restored dock
    // search) hold for the token instead of going out bare and being refused.
    expectAuthToken();
    initWebAuth(config, (login) => {
        showWebAuthGate(() => void login());
    }).catch(onError);
};

/**
 * Website-path auth: acquires a Splice access token from Auth0 and feeds it
 * into the same auth-token signal the plugin bridge uses. Called on boot for
 * non-embedded website sessions when a client ID is configured.
 *
 * Flow: handle a login redirect callback if present, then try a silent token
 * (SSO cookie / cached refresh token). If Auth0 requires interaction,
 * `onLoginRequired` is invoked with a `login` callback that starts the
 * redirect flow — the caller renders the login gate around it.
 */
export const initWebAuth = async (
    config: WebAuthConfig,
    onLoginRequired: (login: () => Promise<void>) => void,
): Promise<void> => {
    const auth0 = await createAuth0Client({
        domain: config.domain,
        clientId: config.clientId,
        // Matches the Splice plugin browser apps. Survives full-page
        // navigations between Labs apps; revisit if the token audience or
        // hosting changes.
        cacheLocation: "localstorage",
        useRefreshTokens: true,
        authorizationParams: {
            audience: kAudience,
            redirect_uri: window.location.origin,
        },
    });

    const params = new URLSearchParams(window.location.search);
    if (params.has("code") && params.has("state")) {
        try {
            const result = await auth0.handleRedirectCallback();
            const returnTo = (result.appState as RedirectAppState | undefined)?.returnTo ?? "/";
            // Auth0 always redirects to the origin root, but each app is its
            // own MPA entry — replaceState alone would leave the home page
            // rendered at the app's URL. Navigate when the paths differ; the
            // destination page picks the token up silently from the cache.
            if (returnTo.split("?")[0] !== window.location.pathname) {
                window.location.replace(returnTo);
                return;
            }
            window.history.replaceState({}, "", returnTo);
        } catch {
            // Stale or forged state — fall through to the normal flow below.
            window.history.replaceState({}, "", "/");
        }
    }

    const acquireToken = async (cacheMode: "on" | "off"): Promise<void> => {
        const token = await auth0.getTokenSilently({ cacheMode });
        setAuthToken(token, decodeJwtExp(token));
    };

    // Lets postToServer's 401-retry path fetch a fresh token on the web,
    // mirroring the plugin's RequestAuthToken message.
    registerRequestAuthToken(() => {
        acquireToken("off").catch(() => {
            // Refresh failed (session expired) — the next 401 surfaces to the
            // user via the app's normal error handling.
        });
    });

    registerWebLogout(() => auth0.logout({ logoutParams: { returnTo: window.location.origin } }));

    try {
        await acquireToken("on");
        // The profile claims come from the ID token the SDK already holds, so
        // this is a cache read, not a round trip.
        const user = await auth0.getUser();
        setWebAccount(
            user === undefined
                ? undefined
                : {
                      name: user.name,
                      nickname: user.nickname,
                      email: user.email,
                      picture: user.picture,
                  },
        );
        // The Splice username and avatar come from the gateway; the menu
        // shows the Auth0 profile in the meantime.
        void refreshSpliceProfile();
    } catch {
        onLoginRequired(() =>
            auth0.loginWithRedirect({
                appState: {
                    returnTo: window.location.pathname + window.location.search,
                } satisfies RedirectAppState,
            }),
        );
    }
};
